import React, { FC, useEffect, useState, useRef } from 'react';
import replyIcon from 'assets/svg/reply icon.svg';
import deleteIcon from 'assets/svg/delete icon.svg';
import pin from 'assets/svg/pin.svg';
import Styles from 'components/chatContent/messageBox/MessageBox.module.scss';
import { IMAGE_URL } from 'store/services/URL';
import { deleteMessage, getImages, deleteImage, updateMessageStatus, deleteThreadMessage } from 'store/services/firebase-broadcast';
import moment from 'moment';
import { formatMessageWithLineBreaks, getMessageTime } from 'helpers/utils';
import { useDispatch } from 'react-redux';
import { openImageViewer } from 'store/reducers/imageViewer.reducer';
import { Spinner } from 'react-bootstrap';

interface MessageBoxProps {
  cssClass?: string;
  item: any;
  index?: number;
  viewReplies?: Function;
  replyItems?: Function;
  actions: boolean;
  selected?: any;
  isThread?: boolean;
  type: string;
  chatsPin?: any;
  pinCount?: any;
  parentMessageId?: string;
  threadIndex?: number;
}

const MessageBox: FC<MessageBoxProps> = (props: MessageBoxProps) => {
  const {
    cssClass = "",
    item,
    viewReplies = () => {},
    replyItems = () => {},
    actions = false,
    selected = null,
    isThread = false,
    type,
    chatsPin = [],
    pinCount = 0,
    parentMessageId,
    threadIndex
  } = props;
  const isMountedRef = useRef(true);
  const [fileType, setFileType] = useState('image');
  const [attachment, setAttachment] = useState('');
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const dispatch = useDispatch();
  const [hover, setHover] = useState(item?.pin ?? false);
  const handleShowReplies = () => {
    if (viewReplies) viewReplies(true);
    if (replyItems) replyItems(item);
  };

  const getTime = (time: string) => {
    // const amPm = time.split(' ');
    // const timeStr = time.split(':');
    // return timeStr[0] + ':' + timeStr[1] + ' ' + amPm[1];
    if (time) {
       return new Date(time).toLocaleString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
       });
    } else {
      return '';
    }
  };

  const handleDelete = async () => {
    if (!selected?.pk) return;

    if (isThread && parentMessageId !== undefined && threadIndex !== undefined) {
      try {
        await deleteThreadMessage(selected.pk, parentMessageId, threadIndex);
      } catch (error) {
        console.error('Failed to delete thread message:', error);
      }
      return;
    }

    const msgPath = `${selected.pk}/messages/${item.id}`;
    if (item.attachments?.length > 0 && item.attachments[0]?.id) {
      const imgPath = `${selected.pk}/${item.attachments[0].id}`;
      deleteImage(imgPath).then();
    }
    deleteMessage(msgPath).then();
    return '';
  };

  const handleRemoveParticipant = () => {
    if (!selected?.pk) return;
    const msgPath = `${selected.pk}/participants/${item.id}`;
    deleteMessage(msgPath).then();
    return '';
  };

  useEffect(() => {
    // Load attachments for all message types including threads
    if (item.attachments && item.attachments.length > 0 && item.attachments[0]?.id) {
      setImageLoading(true);
      setImageError(false);
      setAspectRatio(null);
      
      getImages(selected.pk, item.attachments[0].id).then((e) => {
        if (isMountedRef.current && e !== undefined && e.length > 0) {
          const imageUrl = e[0];
          setAttachment(imageUrl);
          
          // Preload image to get aspect ratio
          const img = new Image();
          img.onload = () => {
            if (isMountedRef.current) {
              const ratio = img.naturalWidth / img.naturalHeight;
              setAspectRatio(ratio);
              // Don't set loading to false yet - wait for actual img tag to load
            }
          };
          img.onerror = () => {
            if (isMountedRef.current) {
              setImageError(true);
              setImageLoading(false);
            }
          };
          img.src = imageUrl;
        } else {
          setImageLoading(false);
        }
      }).catch((error) => {
        // Silently handle missing attachment files
        console.warn('Failed to load attachment:', error);
        setImageError(true);
        setImageLoading(false);
      });
    } else {
      setImageLoading(false);
    }
  }, [item, selected.pk]);

  useEffect(() => {
    const fileName = item.attachments?.length > 0 ? item.attachments[0]?.name : '';
    // const ext = fileName.substring(fileName.lastIndexOf('.'));
    const ext = fileName?.split('.').pop() || '';
    if (ext === 'MP4') {
      setFileType('video');
    }
  }, [item]);

  useEffect(() => {
    setAttachment(attachment);
  }, [attachment]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const OnPressPin = () => {
    if (!selected?.host || user.pk !== selected.host.pk) {
      return;
    }
    if ( chatsPin?.length < 3 || hover === true ) {
      const data = {
        roomId: selected.pk,
        pin: !hover,
        timeStamp: !hover ? moment().format(): "",
        messageId: item.id
      };
      updateMessageStatus(data)
    };
    setHover(!hover);
  }
  
  const handleImageClick = () => {
    if (attachment && fileType === 'image') {
      dispatch(openImageViewer({ imageUrl: attachment, imageName: item.attachments?.[0]?.name }));
    }
  };

  return (
    <>
      <div 
        data-message-id={item.id || item.pk}
        className={`${item.pin === true ? Styles['pinned'+pinCount]: ''}  ${Styles.message} ${cssClass} ${!actions && Styles.padding}`} 
      >
        {!actions && (
          <div className={Styles.actions}>
            {!isThread && <img src={replyIcon} alt="reply icon" onClick={handleShowReplies} onKeyUp={handleShowReplies} role="button" tabIndex={0} />} 
            {(item.sender.id === user.pk || user.pk === selected.host.pk) && <img src={deleteIcon} alt="reply icon" onClick={handleDelete} onKeyUp={handleDelete} role="button" tabIndex={0} />}
            {/* {(user.pk === selected.host.pk) && <a onClick={handleRemoveParticipant} onKeyUp={handleRemoveParticipant} role="button" tabIndex={0} >Remove</a>} */}
          </div>
        )}
        <img src={`${IMAGE_URL}${item.sender.image}`} alt="user" className={Styles.userImage} />
        <div className={Styles.userDetails}>
          <div className={Styles.detailsInner}>
            <h5 className="my-0">{item.sender.name}</h5>
            <span className="fontFifteen font-weight-normal input-color">{getTime(new Date(getMessageTime(item)).toISOString())}</span>
            {item.sender.isHost && <span className={`badge text-white ${Styles.hostBadge}`}>Host</span>}
            <div onClick={OnPressPin} onKeyPress={OnPressPin} role="button" tabIndex={0}>
              <img className={`${item.pin ? Styles.pin : null} ${selected?.host && user.pk === selected.host.pk && Styles.hoverPin} `} src={pin} alt="pin"/>
            </div>
          </div>
          <p 
            className="fontFifteen text-black font-weight-normal mb-0"
            dangerouslySetInnerHTML={{ __html: formatMessageWithLineBreaks(item?.message) }}
          />
          {item.threads && item.threads.length > 0 && (
            <div onClick={handleShowReplies} onKeyPress={handleShowReplies} role="button" tabIndex={0}>
              <h6 className="text-uppercase fontFourteen mb-0 pt-2 text-blue font-weight-500">View Replies</h6>
            </div>
          )}
          {attachment && item.attachments && item.attachments.length > 0 && item.attachments[0] && (
            <div className="d-flex flex-column">
              <span className={Styles.uploadedImageDetails}>{item.attachments[0].name}</span>
              {fileType === 'image' ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  {imageLoading && (
                    <div 
                      className={Styles.uploadedImage}
                      style={{
                        maxWidth: '300px',
                        width: 'auto',
                        aspectRatio: aspectRatio ? `${aspectRatio}` : '4/3',
                        backgroundColor: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '5px'
                      }}
                    >
                      <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                    </div>
                  )}
                  {imageError && (
                    <div 
                      className={Styles.uploadedImage}
                      style={{
                        maxWidth: '300px',
                        width: '300px',
                        aspectRatio: '4/3',
                        backgroundColor: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '5px',
                        color: '#999',
                        fontSize: '12px',
                        padding: '20px'
                      }}
                    >
                      Failed to load image
                    </div>
                  )}
                  {!imageError && (
                    <img 
                      src={attachment} 
                      alt="attachment" 
                      className={Styles.uploadedImage} 
                      onClick={handleImageClick} 
                      style={{ 
                        cursor: 'pointer',
                        maxWidth: '300px',
                        height: 'auto',
                        opacity: imageLoading ? 0 : 1,
                        transition: 'opacity 0.3s ease-in-out',
                        display: imageLoading ? 'none' : 'block'
                      }} 
                      onLoad={() => setImageLoading(false)}
                      onError={() => {
                        setImageError(true);
                        setImageLoading(false);
                      }}
                    />
                  )}
                </div>
              ) : (
                <video className={Styles.uploadedVideo} controls>
                  <source src={attachment} type="video/mp4" />
                </video>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
export default React.memo(MessageBox);
